import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider }         from './context/ToastContext';
import { ConfirmProvider }       from './components/common/ConfirmDialog';
import { NotificationProvider }  from './context/NotificationContext';
import ProtectedRoute            from './components/common/ProtectedRoute';
import Layout                    from './components/layout/Layout';

import Login           from './pages/auth/Login/Login';
import ChangePassword  from './pages/common/ChangePassword/ChangePassword';
import Notifications   from './pages/common/Notification/Notification';

import Dashboard        from './pages/admin/Dashboard/Dashboard';
import Colleges        from './pages/admin/ViewCollege/ViewCollege';
import Sessions        from './pages/admin/Sessions/Sessions';
import Classes         from './pages/admin/Classes/Classes';
import AddDepartment   from './pages/admin/AddDepartment/AddDepartment';
import AddEmployee     from './pages/admin/AddEmployee/AddEmployee';
import PromoteStudents from './pages/admin/PromoteStudents/PromoteStudents';
import AddStudent     from './pages/admin/Students/AddStudent/AddStudent';
import ViewStudent    from './pages/admin/Students/ViewStudent/ViewStudent';
import StudentDetails from './pages/admin/Students/StudentDetails/StudentDetails';
import StudentData    from './pages/admin/Students/StudentData/StudentData';
import Attendance      from './pages/admin/Attendance/Attendance/Attendance';
import Holidays        from './pages/admin/Attendance/Holidays/Holidays';
import AttendanceList  from './pages/admin/Attendance/AttendanceList/AttendanceList';
import CreateSalary    from './pages/admin/Salary/CreateSalary/CreateSalary';
import Salary          from './pages/admin/Salary/Salary/Salary';
import SalaryReceipt   from './pages/admin/Salary/PaymentVoucher/PaymentVoucher';
import AdvanceSalary   from './pages/admin/Salary/AdvanceSalary/AdvanceSalary';
import AdvanceSalaryLog from './pages/admin/Salary/AdvanceSalaryLog/AdvanceSalaryLog';
import PaySalary       from './pages/admin/Salary/PaySalary/PaySalary';
import SalaryLog       from './pages/admin/Salary/SalaryLog/SalaryLog';
import FeeStructure    from './pages/admin/FeeStructure/FeeStructure';
import Transport       from './pages/admin/Transport/Transport';
import AddHead         from './pages/admin/AddHead/AddHead';
import AddBank         from './pages/admin/AddBank/AddBank';
import AddExpense      from './pages/admin/AddExpense/AddExpense';
import ExpenseBook     from './pages/admin/ExpenseBook/ExpenseBook';
import AddNews         from './pages/admin/News/AddNews/AddNews';
import ViewNews        from './pages/admin/News/ViewNews/ViewNews';
import ReceiptLog      from './pages/admin/ReceiptLog/ReceiptLog';

import Defaulters      from './pages/clerk/Defaulter/Defaulter';
import FeeCollection   from './pages/clerk/FeeCollection/FeeCollection';
import FeeCollectionLog from './pages/clerk/FeeCollectionLog/FeeCollectionLog';
import FeeReceipt      from './pages/clerk/FeeReceipt/FeeReceipt';
import DailyReport     from './pages/clerk/DailyReport/DailyReport';
import TotalDueList    from './pages/clerk/TotalDueList/TotalDueList';

import AlertLog        from './pages/superadmin/AlertLog/AlertLog';


// ── Control Panel (src/ControlPanel/) ──
import CPLoginPage      from './ControlPanel/CPLogin';
import CPGuardPage      from './ControlPanel/CPGuard';
import CPAddCollege     from './ControlPanel/AddCollege/AddCollege';
import CPAddAdmin       from './ControlPanel/AddAdmin/AddAdmin';
import CPAddSuperAdmin  from './ControlPanel/AddSuperAdmin/AddSuperAdmin';
import CPDataManager   from './ControlPanel/DataManager/DataManager';
import CPRecycleBin    from './ControlPanel/RecycleBin/RecycleBin';
import CPChangePassword from './ControlPanel/ChangePassword/CPChangePassword';

import './App.css';
import TallyReceipt from './pages/admin/TallyExport/Receipt/Receipt';
import TallyJournalSalary from './pages/admin/TallyExport/JournalSalary/JournalSalary';
import TallyExpense from './pages/admin/TallyExport/Expense/Expense';
import TallySalary from './pages/admin/TallyExport/Salary/Salary';
import TallyJournalFees from './pages/admin/TallyExport/JournalFees/JournalFees';  

const ROLE_HOME = { superadmin: '/superadmin', admin: '/admin', clerk: '/clerk', collegeadmin: '/collegeadmin' };
const ALL = ['superadmin', 'admin', 'clerk', 'collegeadmin'];

const RoleRedirect = () => {
  const { user } = useAuth();
  return <Navigate to={user ? ROLE_HOME[user.role] : '/login'} replace />;
};

const Guard = ({ children, roles = ['admin'] }) => (
  <ProtectedRoute roles={roles}><Layout>{children}</Layout></ProtectedRoute>
);

const App = () => (
  <ToastProvider>
    <ConfirmProvider>
      <AuthProvider>
        <NotificationProvider>
          <BrowserRouter
  future={{
    v7_startTransition: true,
    v7_relativeSplatPath: true
  }}
>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/"      element={<RoleRedirect />} />

              {/* ── Shared (all roles except clerk for change password) ── */}
              <Route path="/change-password" element={<Guard roles={['superadmin', 'admin', 'collegeadmin']}><ChangePassword /></Guard>} />
              <Route path="/notifications"   element={<Guard roles={ALL}><Notifications /></Guard>} />

              {/* ── Super Admin ── */}
              <Route path="/superadmin"              element={<Guard roles={['superadmin']}><Dashboard /></Guard>} />
              <Route path="/superadmin/defaulters"   element={<Guard roles={['superadmin']}><Defaulters /></Guard>} />
              <Route path="/superadmin/total-due-list" element={<Guard roles={['superadmin']}><TotalDueList /></Guard>} />
              <Route path="/superadmin/daily-report" element={<Guard roles={['superadmin']}><DailyReport /></Guard>} />
              <Route path="/superadmin/expense-book" element={<Guard roles={['superadmin']}><ExpenseBook /></Guard>} />
              <Route path="/superadmin/alert-log"    element={<Guard roles={['superadmin']}><AlertLog /></Guard>} />
              <Route path="/superadmin/news/view"    element={<Guard roles={['superadmin']}><ViewNews /></Guard>} />

              {/* ── Admin ── */}
              <Route path="/admin"             element={<Guard><Dashboard /></Guard>} />
              <Route path="/admin/sessions"    element={<Guard><Sessions /></Guard>} />
              <Route path="/admin/colleges"    element={<Guard><Colleges /></Guard>} />
              <Route path="/admin/classes"     element={<Guard><Classes /></Guard>} />
              <Route path="/admin/departments"     element={<Guard><AddDepartment /></Guard>} />
              <Route path="/admin/add-department"  element={<Guard><AddDepartment /></Guard>} />
              <Route path="/admin/employees"      element={<Guard><AddEmployee /></Guard>} />
              <Route path="/admin/add-employee"   element={<Guard><AddEmployee /></Guard>} />
              <Route path="/admin/students/view"      element={<Guard><ViewStudent /></Guard>} />
              <Route path="/admin/students/view/:id"  element={<Guard><StudentDetails /></Guard>} />
              <Route path="/admin/students/data/:id"  element={<Guard><StudentData /></Guard>} />
              <Route path="/admin/students/add"       element={<Guard><AddStudent /></Guard>} />
              <Route path="/admin/promote-students"    element={<Guard><PromoteStudents /></Guard>} />
              <Route path="/admin/transport"        element={<Guard><Transport /></Guard>} />
              <Route path="/admin/fees"              element={<Guard><FeeStructure /></Guard>} />
              <Route path="/admin/fees/create"       element={<Guard><FeeStructure /></Guard>} />
              <Route path="/admin/attendance"         element={<Guard><Attendance /></Guard>} />
              <Route path="/admin/holidays"             element={<Guard><Holidays /></Guard>} />
              <Route path="/admin/attendance-list"      element={<Guard><AttendanceList /></Guard>} />
              <Route path="/admin/salary"               element={<Guard><Salary /></Guard>} />
              <Route path="/admin/salary/advance"              element={<Guard><AdvanceSalary /></Guard>} />
              <Route path="/admin/salary/advance/log/:empId"   element={<Guard><AdvanceSalaryLog /></Guard>} />
              <Route path="/admin/salary/create"        element={<Guard><CreateSalary /></Guard>} />
              <Route path="/admin/salary/pay"           element={<Guard><PaySalary /></Guard>} />
              <Route path="/admin/salary/log/:empId"    element={<Guard><SalaryLog /></Guard>} />
              <Route path="/admin/salary/receipt"       element={<Guard><SalaryReceipt /></Guard>} />
              <Route path="/admin/salary/view"          element={<Guard><SalaryReceipt /></Guard>} />
              <Route path="/admin/fee-collection-log" element={<Guard roles={['admin']}><FeeCollectionLog /></Guard>} />
              <Route path="/admin/fee-receipt"      element={<Guard roles={['admin']}><FeeReceipt /></Guard>} />
              <Route path="/admin/fee-collection"   element={<Guard roles={['admin']}><FeeCollection /></Guard>} />
              <Route path="/admin/defaulters"       element={<Guard roles={['admin']}><Defaulters /></Guard>} />
              <Route path="/admin/total-due-list"   element={<Guard roles={['admin']}><TotalDueList /></Guard>} />
              <Route path="/admin/daily-report"     element={<Guard roles={['admin']}><DailyReport /></Guard>} />
              <Route path="/admin/expense-heads"    element={<Guard><AddHead /></Guard>} />
              <Route path="/admin/banks"            element={<Guard><AddBank /></Guard>} />
              <Route path="/admin/add-expense"      element={<Guard><AddExpense /></Guard>} />
              <Route path="/admin/expense-book"     element={<Guard><ExpenseBook /></Guard>} />
              <Route path="/admin/news/add"        element={<Guard><AddNews /></Guard>} />
              <Route path="/admin/news/view"       element={<Guard><ViewNews /></Guard>} />
              <Route path="/admin/fee/structure"    element={<Guard><FeeStructure /></Guard>} />
              <Route path="/admin/receipt-log"       element={<Guard roles={['admin']}><ReceiptLog /></Guard>} />
              <Route path="/admin/alert-log"         element={<Guard roles={['admin']}><AlertLog /></Guard>} />
              <Route path="/admin/tally-receipt"         element={<Guard roles={['admin']}><TallyReceipt /></Guard>} />
              <Route path="/admin/tally-journal-salary"         element={<Guard roles={['admin']}><TallyJournalSalary /></Guard>} />
              <Route path="/admin/tally-expense"         element={<Guard roles={['admin']}><TallyExpense /></Guard>} />
              <Route path="/admin/tally-salary"         element={<Guard roles={['admin']}><TallySalary /></Guard>} />
              <Route path="/admin/tally-journal-fees"         element={<Guard roles={['admin']}><TallyJournalFees /></Guard>} />

              {/* ── Clerk ── */}
              <Route path="/clerk"            element={<Guard roles={['clerk']}><Dashboard /></Guard>} />
              <Route path="/clerk/sessions"   element={<Guard roles={['clerk']}><Sessions /></Guard>} />
              <Route path="/clerk/banks"      element={<Guard roles={['clerk']}><AddBank /></Guard>} />
              <Route path="/clerk/students/add"       element={<Guard roles={['clerk']}><AddStudent /></Guard>} />
              <Route path="/clerk/students/view"      element={<Guard roles={['clerk']}><ViewStudent readOnly /></Guard>} />
              <Route path="/clerk/students/data/:id"  element={<Guard roles={['clerk']}><StudentData /></Guard>} />
              <Route path="/clerk/fee-collection"   element={<Guard roles={['clerk']}><FeeCollection /></Guard>} />
              <Route path="/clerk/fee-collection-log" element={<Guard roles={['clerk']}><FeeCollectionLog /></Guard>} />
              <Route path="/clerk/fee-receipt"      element={<Guard roles={['clerk']}><FeeReceipt /></Guard>} />
              <Route path="/clerk/defaulters"       element={<Guard roles={['clerk']}><Defaulters /></Guard>} />
              <Route path="/clerk/daily-report"     element={<Guard roles={['clerk']}><DailyReport /></Guard>} />
              <Route path="/clerk/total-due-list"    element={<Guard roles={['clerk', 'admin', 'collegeadmin']}><TotalDueList /></Guard>} />
              <Route path="/clerk/receipt-log"      element={<Guard roles={['clerk']}><ReceiptLog /></Guard>} />
              <Route path="/clerk/add-expense"      element={<Guard roles={['clerk']}><AddExpense /></Guard>} />
              <Route path="/clerk/expense-book"     element={<Guard roles={['clerk']}><ExpenseBook /></Guard>} />
              <Route path="/clerk/news/view"        element={<Guard roles={['clerk']}><ViewNews /></Guard>} />

              {/* ── College Admin ── */}
              <Route path="/collegeadmin"             element={<Guard roles={['collegeadmin']}><Dashboard /></Guard>} />
              <Route path="/collegeadmin/students/view"    element={<Guard roles={['collegeadmin']}><ViewStudent /></Guard>} />
              <Route path="/collegeadmin/students/view/:id" element={<Guard roles={['collegeadmin']}><StudentDetails /></Guard>} />
              <Route path="/collegeadmin/students/data/:id" element={<Guard roles={['collegeadmin']}><StudentData /></Guard>} />
              <Route path="/collegeadmin/students/add"     element={<Guard roles={['collegeadmin']}><AddStudent /></Guard>} />
              <Route path="/collegeadmin/promote-students"  element={<Guard roles={['collegeadmin']}><PromoteStudents /></Guard>} />
              <Route path="/collegeadmin/sessions"    element={<Guard roles={['collegeadmin']}><Sessions /></Guard>} />
              <Route path="/collegeadmin/employees"      element={<Guard roles={['collegeadmin']}><AddEmployee /></Guard>} />
              <Route path="/collegeadmin/add-employee"   element={<Guard roles={['collegeadmin']}><AddEmployee /></Guard>} />
              <Route path="/collegeadmin/classes"     element={<Guard roles={['collegeadmin']}><Classes /></Guard>} />
              <Route path="/collegeadmin/departments"     element={<Guard roles={['collegeadmin']}><AddDepartment /></Guard>} />
              <Route path="/collegeadmin/add-department"  element={<Guard roles={['collegeadmin']}><AddDepartment /></Guard>} />
              <Route path="/collegeadmin/fees"      element={<Guard roles={['collegeadmin']}><FeeStructure /></Guard>} />
              <Route path="/collegeadmin/fee-collection"        element={<Guard roles={['collegeadmin']}><FeeCollection /></Guard>} />
              <Route path="/collegeadmin/fee-collection-log" element={<Guard roles={['collegeadmin']}><FeeCollectionLog /></Guard>} />
              <Route path="/collegeadmin/fee-receipt"      element={<Guard roles={['collegeadmin']}><FeeReceipt /></Guard>} />
              <Route path="/collegeadmin/defaulters"       element={<Guard roles={['collegeadmin']}><Defaulters /></Guard>} />
              <Route path="/collegeadmin/expense-heads"    element={<Guard roles={['collegeadmin']}><AddHead /></Guard>} />
              <Route path="/collegeadmin/banks"            element={<Guard roles={['collegeadmin']}><AddBank /></Guard>} />
              <Route path="/collegeadmin/add-expense"      element={<Guard roles={['collegeadmin']}><AddExpense /></Guard>} />
              <Route path="/collegeadmin/expense-book"     element={<Guard roles={['collegeadmin']}><ExpenseBook /></Guard>} />
              <Route path="/collegeadmin/transport"  element={<Guard roles={['collegeadmin']}><Transport /></Guard>} />
              <Route path="/collegeadmin/attendance"      element={<Guard roles={['collegeadmin']}><Attendance /></Guard>} />
              <Route path="/collegeadmin/holidays"         element={<Guard roles={['collegeadmin']}><Holidays /></Guard>} />
              <Route path="/collegeadmin/attendance-list"  element={<Guard roles={['collegeadmin']}><AttendanceList /></Guard>} />
              <Route path="/collegeadmin/salary"            element={<Guard roles={['collegeadmin']}><Salary /></Guard>} />
              <Route path="/collegeadmin/salary/advance"              element={<Guard roles={['collegeadmin']}><AdvanceSalary /></Guard>} />
              <Route path="/collegeadmin/salary/advance/log/:empId"   element={<Guard roles={['collegeadmin']}><AdvanceSalaryLog /></Guard>} />
              <Route path="/collegeadmin/salary/create"     element={<Guard roles={['collegeadmin']}><CreateSalary /></Guard>} />
              <Route path="/collegeadmin/salary/pay"        element={<Guard roles={['collegeadmin']}><PaySalary /></Guard>} />
              <Route path="/collegeadmin/salary/log/:empId"  element={<Guard roles={['collegeadmin']}><SalaryLog /></Guard>} />
              <Route path="/collegeadmin/salary/receipt"      element={<Guard roles={['collegeadmin']}><SalaryReceipt /></Guard>} />
              <Route path="/collegeadmin/salary/view"         element={<Guard roles={['collegeadmin']}><SalaryReceipt /></Guard>} />
              <Route path="/collegeadmin/receipt-log"   element={<Guard roles={['collegeadmin']}><ReceiptLog /></Guard>} />
              <Route path="/collegeadmin/daily-report"        element={<Guard roles={['collegeadmin']}><DailyReport /></Guard>} />
              <Route path="/collegeadmin/total-due-list"   element={<Guard roles={['collegeadmin']}><TotalDueList /></Guard>} />
              <Route path="/collegeadmin/news/add"            element={<Guard roles={['collegeadmin']}><AddNews /></Guard>} />
              <Route path="/collegeadmin/news/view"           element={<Guard roles={['collegeadmin']}><ViewNews /></Guard>} />

              {/* ── College Control Panel ── */}
              <Route path="/cp/login"    element={<CPLoginPage />} />
              <Route path="/cp/colleges"    element={<CPGuardPage><CPAddCollege /></CPGuardPage>} />
              <Route path="/cp/admin"       element={<CPGuardPage><CPAddAdmin /></CPGuardPage>} />
              <Route path="/cp/superadmin"  element={<CPGuardPage><CPAddSuperAdmin /></CPGuardPage>} />
              <Route path="/cp/data"        element={<CPGuardPage><CPDataManager /></CPGuardPage>} />
              <Route path="/cp/recycle-bin" element={<CPGuardPage><CPRecycleBin /></CPGuardPage>} />
              <Route path="/cp/change-password" element={<CPGuardPage><CPChangePassword /></CPGuardPage>} />
              <Route path="/cp"             element={<Navigate to="/cp/colleges" replace />} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </NotificationProvider>
      </AuthProvider>
    </ConfirmProvider>
  </ToastProvider>
);

export default App;
